// UnifiedFlowOverlay.jsx
import React, { memo, useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { Chart as ChartJS } from "chart.js";
import {
  buildFallbackLoopTimesSeconds,
  clamp,
  computeLoopPeaks, // add this
  findActiveLoopIndexBySecond,
  fmtNumber,
  normalizeTimesToSeconds,
  sortLoopsByIndex,
} from "../../utils/loopsHelpers";
import { Switch } from "../Switch";
import { getPrimaryColour } from "../../utils/getPrimaryColour";

/**
 * Draw peak split lines (vExPeak / vInPeak) for up to 2 overlays (active + prev).
 * Provide via options.plugins.overlayPeakLinesPlugin.items = [{ peaks, color, alpha }, ...]
 */
const overlayPeakLinesPlugin = {
  id: "overlayPeakLinesPlugin",
  afterDatasetsDraw(chart, args, opts) {
    if (!opts?.enabled) return;

    const { ctx, chartArea, scales } = chart;
    const x = scales.x;
    const y = scales.y;
    if (!x || !y) return;

    const y0 = y.getPixelForValue(0);
    const items = Array.isArray(opts.items) ? opts.items : [];

    for (const item of items) {
      const peaks = item?.peaks;
      if (!peaks?.hasPeaks) continue;

      const xEx = x.getPixelForValue(peaks.vExPeak);
      const xIn = x.getPixelForValue(peaks.vInPeak);
      const yEx = y.getPixelForValue(peaks.fExPeak);
      const yIn = y.getPixelForValue(peaks.fInPeak);

      ctx.save();
      ctx.strokeStyle = item?.color || getPrimaryColour();
      ctx.globalAlpha = item?.alpha ?? 0.6;
      ctx.setLineDash(item?.dash ?? [6, 4]);
      ctx.lineWidth = item?.lineWidth ?? 1.5;

      // Exhale peak line (x=vExPeak, from baseline to fExPeak)
      if (
        Number.isFinite(xEx) &&
        xEx >= chartArea.left &&
        xEx <= chartArea.right
      ) {
        ctx.beginPath();
        ctx.moveTo(xEx, y0);
        ctx.lineTo(xEx, yEx);
        ctx.stroke();
      }

      // Inhale peak line (x=vInPeak, from baseline to fInPeak)
      if (
        Number.isFinite(xIn) &&
        xIn >= chartArea.left &&
        xIn <= chartArea.right
      ) {
        ctx.beginPath();
        ctx.moveTo(xIn, y0);
        ctx.lineTo(xIn, yIn);
        ctx.stroke();
      }

      ctx.restore();
    }
  },
};

/**
 * Very low opacity quadrant fills for up to 2 overlays (active + prev).
 * Provide via options.plugins.overlayShadingPlugin.items = [{ points, peaks, colors, alpha }, ...]
 *
 * This reuses your split-with-boundary approach so the fill meets the split line exactly.
 */
const overlayShadingPlugin = {
  id: "overlayShadingPlugin",
  beforeDatasetsDraw(chart, args, opts) {
    if (!opts?.enabled) return;

    const { ctx, scales } = chart;
    const x = scales.x;
    const y = scales.y;
    if (!x || !y) return;

    const y0 = y.getPixelForValue(0);
    const items = Array.isArray(opts.items) ? opts.items : [];

    function splitWithBoundary(sortedPts, splitX) {
      if (!Array.isArray(sortedPts) || sortedPts.length < 2)
        return { left: [], right: [] };
      if (!Number.isFinite(splitX))
        return { left: sortedPts.slice(), right: [] };

      const minX = sortedPts[0].x;
      const maxX = sortedPts[sortedPts.length - 1].x;

      if (splitX <= minX)
        return { left: [sortedPts[0]], right: sortedPts.slice() };
      if (splitX >= maxX)
        return {
          left: sortedPts.slice(),
          right: [sortedPts[sortedPts.length - 1]],
        };

      let i = -1;
      for (let k = 0; k < sortedPts.length - 1; k++) {
        const a = sortedPts[k];
        const b = sortedPts[k + 1];
        if (a.x <= splitX && splitX <= b.x) {
          i = k;
          break;
        }
      }

      if (i === -1) {
        const left = sortedPts.filter((p) => p.x <= splitX);
        const right = sortedPts.filter((p) => p.x >= splitX);
        return { left, right };
      }

      const a = sortedPts[i];
      const b = sortedPts[i + 1];

      let boundary;
      if (a.x === splitX) boundary = { x: a.x, y: a.y };
      else if (b.x === splitX) boundary = { x: b.x, y: b.y };
      else {
        const t = (splitX - a.x) / (b.x - a.x || 1);
        boundary = { x: splitX, y: a.y + t * (b.y - a.y) };
      }

      const left = sortedPts.slice(0, i + 1);
      const right = sortedPts.slice(i + 1);

      if (left.length === 0 || left[left.length - 1].x !== boundary.x)
        left.push(boundary);
      else left[left.length - 1] = boundary;

      if (right.length === 0 || right[0].x !== boundary.x)
        right.unshift(boundary);
      else right[0] = boundary;

      return { left, right };
    }

    function drawFill(pts, fillStyle, alpha) {
      if (!pts || pts.length < 2) return;

      ctx.save();
      ctx.fillStyle = fillStyle;
      ctx.globalAlpha = alpha;

      ctx.beginPath();
      const p0 = pts[0];
      ctx.moveTo(x.getPixelForValue(p0.x), y.getPixelForValue(p0.y));

      for (let i = 1; i < pts.length; i++) {
        const p = pts[i];
        ctx.lineTo(x.getPixelForValue(p.x), y.getPixelForValue(p.y));
      }

      const last = pts[pts.length - 1];
      ctx.lineTo(x.getPixelForValue(last.x), y0);
      ctx.lineTo(x.getPixelForValue(p0.x), y0);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    for (const item of items) {
      const peaks = item?.peaks;
      const points = Array.isArray(item?.points) ? item.points : [];
      if (!peaks?.hasPeaks || points.length < 2) continue;

      const alpha = item?.alpha ?? 0.08;

      const ex = points
        .filter((p) => p.y > 0 && Number.isFinite(p.x) && Number.isFinite(p.y))
        .slice()
        .sort((a, b) => a.x - b.x);

      const inn = points
        .filter((p) => p.y < 0 && Number.isFinite(p.x) && Number.isFinite(p.y))
        .slice()
        .sort((a, b) => a.x - b.x);

      if (ex.length < 2 || inn.length < 2) continue;

      const colors = item?.colors || {};

      // Exhale split at vExPeak
      const { left: exLeft, right: exRight } = splitWithBoundary(
        ex,
        peaks.vExPeak,
      );
      drawFill(exLeft, colors.exLeft || "#f8a0d6", alpha);
      drawFill(exRight, colors.exRight || "#a56498", alpha);

      // Inhale split at vInPeak
      const { left: inLeft, right: inRight } = splitWithBoundary(
        inn,
        peaks.vInPeak,
      );
      // Your convention: right = x >= vInPeak
      drawFill(inRight, colors.inRight || "#744267", alpha);
      drawFill(inLeft, colors.inLeft || "#553150", alpha);
    }
  },
};

// Register once (safe if called multiple times)
ChartJS.register(overlayPeakLinesPlugin, overlayShadingPlugin);

function lastIndexAtOrBefore(timesSortedAsc, t) {
  let lo = 0;
  let hi = timesSortedAsc.length - 1;
  let ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (timesSortedAsc[mid] <= t) {
      ans = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return ans;
}

function computeFixedBoundsFromLoops(loops, padFrac = 0.05) {
  let xMin = Infinity,
    xMax = -Infinity,
    yMin = Infinity,
    yMax = -Infinity;

  for (const loop of loops || []) {
    const pts = Array.isArray(loop?.points) ? loop.points : [];
    for (const p of pts) {
      const x = Number(p?.x);
      const y = Number(p?.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      if (x < xMin) xMin = x;
      if (x > xMax) xMax = x;
      if (y < yMin) yMin = y;
      if (y > yMax) yMax = y;
    }
  }

  if (![xMin, xMax, yMin, yMax].every(Number.isFinite)) return null;

  const xRange = xMax - xMin || 1;
  const yRange = yMax - yMin || 1;
  const xPad = xRange * padFrac;
  const yPad = yRange * padFrac;

  return {
    xMin: xMin - xPad,
    xMax: xMax + xPad,
    yMin: yMin - yPad,
    yMax: yMax + yPad,
  };
}

function niceStep(range, targetTicks = 9) {
  if (!Number.isFinite(range) || range <= 0) return 1;
  const raw = range / targetTicks;
  const pow10 = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / pow10;
  let nice;
  if (norm <= 1) nice = 1;
  else if (norm <= 2) nice = 2;
  else if (norm <= 5) nice = 5;
  else nice = 10;
  return nice * pow10;
}

function zeroOnlyTickLabel(v) {
  const num = Number(v);
  if (!Number.isFinite(num)) return "";
  return Math.abs(num) < 1e-9 ? "0" : "";
}

function UnifiedFlowOverlay({
  loops,
  axisBounds,
  durationSeconds,
  times,
  loopTimesS: loopTimesSProp,
  trailCount = 10,
  initialMode = "timeline",
  initialReveal = "progress",
}) {
  const { t } = useTranslation();

  const sorted = useMemo(() => sortLoopsByIndex(loops), [loops]);
  const n = sorted.length;

  const fixedBounds = useMemo(() => {
    const hasAxis =
      axisBounds &&
      [
        axisBounds.xMin,
        axisBounds.xMax,
        axisBounds.yMin,
        axisBounds.yMax,
      ].every(Number.isFinite);
    if (hasAxis) return axisBounds;
    return computeFixedBoundsFromLoops(sorted, 0.05);
  }, [axisBounds, sorted]);

  const durationS = useMemo(() => {
    const d = Number(durationSeconds);
    if (Number.isFinite(d) && d > 0) return d;
    return Math.max(1, n);
  }, [durationSeconds, n]);

  const loopTimesS = useMemo(() => {
    if (Array.isArray(loopTimesSProp) && loopTimesSProp.length) {
      const clean = loopTimesSProp.map(Number).filter(Number.isFinite);
      if (clean.length === n) return clean;
      if (clean.length > n) return clean.slice(0, n);
      if (clean.length > 0 && clean.length < n)
        return buildFallbackLoopTimesSeconds(n, durationS);
    }
    return buildFallbackLoopTimesSeconds(n, durationS);
  }, [loopTimesSProp, n, durationS]);

  const shapeTimesS = useMemo(() => {
    const d = Number.isFinite(durationS) && durationS > 0 ? durationS : 60;
    const tt = normalizeTimesToSeconds(times, d);
    if (tt.length >= 2) {
      let ok = true;
      for (let i = 1; i < tt.length; i++) {
        if (tt[i] < tt[i - 1]) {
          ok = false;
          break;
        }
      }
      return ok ? tt : tt.slice().sort((a, b) => a - b);
    }
    return tt;
  }, [times, durationS]);

  const [mode, setMode] = useState(initialMode);
  const [reveal, setReveal] = useState(initialReveal);

  const [tSec, setTSec] = useState(() => Number(durationSeconds) || 0);
  const [isPlaying, setIsPlaying] = useState(false);

  const [activeIdxManual, setActiveIdxManual] = useState(0);
  const [loopProgress, setLoopProgress] = useState(1);

  const [showFill, setShowFill] = useState(false);

  useEffect(() => {
    setActiveIdxManual((v) => clamp(v, 0, Math.max(0, n - 1)));
  }, [n]);

  useEffect(() => {
    if (mode !== "timeline") setIsPlaying(false);
  }, [mode]);

  const modeOptions = useMemo(
    () => [
      {
        value: "timeline",
        label: t("results.flowLoops.modeTimeline", {
          defaultValue: "Timeline",
        }),
      },
      {
        value: "loop",
        label: t("results.flowLoops.modeLoop", { defaultValue: "Loop" }),
      },
    ],
    [t],
  );

  const revealOptions = useMemo(
    () => [
      {
        value: "progress",
        label: t("results.flowLoops.revealProgress", {
          defaultValue: "Progress",
        }),
      },
      {
        value: "none",
        label: t("results.flowLoops.revealFull", { defaultValue: "Full" }),
      },
    ],
    [t],
  );

  const activeIdx = useMemo(() => {
    if (!n) return 0;
    if (mode === "timeline") {
      return clamp(findActiveLoopIndexBySecond(loopTimesS, tSec), 0, n - 1);
    }
    return clamp(activeIdxManual, 0, n - 1);
  }, [mode, n, loopTimesS, tSec, activeIdxManual]);

  const activeWindow = useMemo(() => {
    if (!n) return { start: 0, end: durationS };
    const start = Number(loopTimesS[activeIdx] ?? 0);
    const end =
      activeIdx + 1 < loopTimesS.length
        ? Number(loopTimesS[activeIdx + 1])
        : durationS;
    const s = Number.isFinite(start) ? start : 0;
    const e = Number.isFinite(end) ? end : durationS;
    return { start: clamp(s, 0, durationS), end: clamp(e, 0, durationS) };
  }, [n, loopTimesS, activeIdx, durationS]);

  const activeVisibleIdx = useMemo(() => {
    if (!n) return -1;
    const pts = Array.isArray(sorted?.[activeIdx]?.points)
      ? sorted[activeIdx].points
      : [];
    const m = pts.length;
    if (!m) return -1;

    if (reveal !== "progress") return m - 1;

    if (mode === "timeline") {
      const { start, end } = activeWindow;
      const loopDur = Math.max(1e-6, end - start);
      const localT = clamp(tSec - start, 0, loopDur);

      const aligned = shapeTimesS.slice(0, m);
      const shapeMax = aligned.length ? aligned[aligned.length - 1] : 0;

      if (!Number.isFinite(shapeMax) || shapeMax <= 0) {
        const frac = localT / loopDur;
        return clamp(Math.floor(frac * (m - 1)), -1, m - 1);
      }

      const shapeT = (localT / loopDur) * shapeMax;
      return clamp(lastIndexAtOrBefore(aligned, shapeT), -1, m - 1);
    }

    return clamp(Math.floor(loopProgress * (m - 1)), -1, m - 1);
  }, [
    n,
    sorted,
    activeIdx,
    reveal,
    mode,
    activeWindow,
    tSec,
    shapeTimesS,
    loopProgress,
  ]);

  useEffect(() => {
    if (mode !== "timeline") return;
    if (!isPlaying) return;

    let raf = 0;
    let last = performance.now();

    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;

      setTSec((prev) => {
        const next = prev + dt;
        if (next >= durationS) {
          setIsPlaying(false);
          return durationS;
        }
        return next;
      });

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mode, isPlaying, durationS]);

  useEffect(() => {
    setTSec((v) => clamp(v, 0, durationS));
  }, [durationS]);

  const onTogglePlay = () => {
    if (mode !== "timeline") return;
    setIsPlaying((prev) => {
      if (!prev && tSec >= durationS - 1e-6) {
        setTSec(0);
        return true;
      }
      return !prev;
    });
  };

  const primary = getPrimaryColour();

  // Build datasets
  const datasets = useMemo(() => {
    if (!n) return [];

    const out = [];
    const tertiary = "#B776B9";
    const grey = "rgba(189,189,189,0.55)";

    for (let idx = 0; idx <= activeIdx; idx += 1) {
      const loop = sorted[idx];
      const pts = Array.isArray(loop?.points) ? loop.points : [];

      const isActive = idx === activeIdx;
      const isPrev = idx === activeIdx - 1;

      const visiblePts = isActive
        ? pts.slice(0, clamp(activeVisibleIdx + 1, 0, pts.length))
        : pts;

      const stroke = isActive ? primary : isPrev ? tertiary : grey;

      out.push({
        label: loop.loopName,
        data: visiblePts,
        parsing: false,
        pointRadius: 0,
        tension: 0.2,
        borderWidth: isActive ? 2.75 : isPrev ? 2.0 : 1.5,
        borderColor: stroke,
        backgroundColor: stroke,
      });
    }

    return out;
  }, [n, sorted, activeIdx, activeVisibleIdx, primary]);
  // NEW: compute peaks + points for ACTIVE and PREV only
  const overlaysForPlugins = useMemo(() => {
    if (!n) return [];

    const items = [];

    // Active (use the same visible slice so fill animates with reveal)
    const activePts = Array.isArray(sorted?.[activeIdx]?.points)
      ? sorted[activeIdx].points
      : [];
    const activeVisiblePts =
      reveal === "progress"
        ? activePts.slice(0, clamp(activeVisibleIdx + 1, 0, activePts.length))
        : activePts;

    const activePeaks = computeLoopPeaks(activeVisiblePts);
    if (activePeaks?.hasPeaks) {
      items.push({
        key: "active",
        color: primary,
        alpha: 0.55,
        peaks: activePeaks,
        points: activeVisiblePts,
        colors: {
          exLeft: "#f8a0d6",
          exRight: "#a56498",
          inLeft: "#553150",
          inRight: "#744267",
        },
      });
    }

    // Previous (full loop)
    const prevIdx = activeIdx - 1;
    if (prevIdx >= 0) {
      const prevPts = Array.isArray(sorted?.[prevIdx]?.points)
        ? sorted[prevIdx].points
        : [];
      const prevPeaks = computeLoopPeaks(prevPts);
      if (prevPeaks?.hasPeaks) {
        items.push({
          key: "prev",
          color: "#B776B9",
          alpha: 0.45,
          peaks: prevPeaks,
          points: prevPts,
          colors: {
            exLeft: "#f8a0d6",
            exRight: "#a56498",
            inLeft: "#553150",
            inRight: "#744267",
          },
        });
      }
    }

    return items;
  }, [n, sorted, activeIdx, reveal, activeVisibleIdx, primary]);

  const xStep = useMemo(() => {
    const b = fixedBounds;
    if (!b) return 1;
    return niceStep((b.xMax ?? 0) - (b.xMin ?? 0), 9);
  }, [fixedBounds]);

  const yStep = useMemo(() => {
    const b = fixedBounds;
    if (!b) return 1;
    return niceStep((b.yMax ?? 0) - (b.yMin ?? 0), 9);
  }, [fixedBounds]);

  const data = useMemo(() => ({ datasets }), [datasets]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const x = ctx.raw?.x;
              const y = ctx.raw?.y;
              return `V: ${fmtNumber(x, 3)} | F: ${fmtNumber(y, 3)}`;
            },
          },
        },

        // your existing ones can stay:
        zeroAxesPlugin: {
          enabled: true,
        color: primary,
          alpha: 0.18,
          lineWidth: 1.2,
          drawX0: true,
          drawY0: true,
        },
        quadrantLabelsPlugin: { enabled: true, color: primary },

        // overlay peak lines + low opacity fill (active + prev only)
        overlayPeakLinesPlugin: {
          enabled: overlaysForPlugins.length > 0,
          items: overlaysForPlugins.map((it) => ({
            peaks: it.peaks,
            color: it.color,
            alpha: it.alpha,
            dash: [6, 4],
            lineWidth: 1.4,
          })),
        },
        overlayShadingPlugin: {
          enabled: showFill && overlaysForPlugins.length > 0,
          items: overlaysForPlugins.map((it) => ({
            peaks: it.peaks,
            points: it.points,
            colors: it.colors,
            alpha: 0.08,
          })),
        },
      },
      scales: {
        x: {
          type: "linear",
          title: {
            display: true,
            text: t("results.flowLoops.volumeAxis", {
              defaultValue: "Volume",
            }),
          },
          min: fixedBounds?.xMin,
          max: fixedBounds?.xMax,
          ticks: {
            stepSize: xStep,
            includeBounds: true,
            autoSkip: true,
            maxRotation: 0,
            minRotation: 0,
            callback: (v) => zeroOnlyTickLabel(v),
          },
        },
        y: {
          title: {
            display: true,
            text: t("results.flowLoops.flowAxis", {
              defaultValue: "Flow",
            }),
          },
          min: fixedBounds?.yMin,
          max: fixedBounds?.yMax,
          ticks: {
            stepSize: yStep,
            includeBounds: true,
            autoSkip: true,
            maxRotation: 0,
            minRotation: 0,
            callback: (v) => zeroOnlyTickLabel(v),
          },
        },
      },
    }),
    [fixedBounds, xStep, yStep, t, overlaysForPlugins, showFill, primary],
  );

  const activeLoopName = sorted?.[activeIdx]?.loopName ?? "";
  if (!n) return null;

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-greyscale-3">
          {t("results.flowLoops.unifiedSub", {
            defaultValue: "{{loopName}}",
            loopName: activeLoopName,
          })}
        </p>

        <div className="flex items-center gap-3">
          <Switch
            checked={showFill}
            onChange={setShowFill}
            label={t("results.flowLoops.fill", { defaultValue: "Fill" })}
          />

          <label className="text-sm text-greyscale-3">
            <select
              className="ml-2 px-2 py-1 rounded-md border border-greyscale-1 text-sm bg-white"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
            >
              {modeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm text-greyscale-3">
            <select
              className="ml-2 px-2 py-1 rounded-md border border-greyscale-1 text-sm bg-white"
              value={reveal}
              onChange={(e) => setReveal(e.target.value)}
            >
              {revealOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="w-full h-80">
        <Line data={data} options={options} />
      </div>

      {mode === "timeline" && (
        <div className="w-full flex items-center justify-between gap-2">
          <button
            type="button"
            className="px-3 py-1 rounded-md border border-greyscale-1 text-sm"
            onClick={onTogglePlay}
          >
            {isPlaying
              ? t("results.flowLoops.pause", { defaultValue: "Pause" })
              : t("results.flowLoops.play", { defaultValue: "Play" })}
          </button>

          <button
            type="button"
            className="px-3 py-1 rounded-md border border-greyscale-1 text-sm"
            onClick={() => {
              setIsPlaying(false);
              setTSec(0);
            }}
          >
            {t("results.flowLoops.restart", { defaultValue: "Restart" })}
          </button>
        </div>
      )}

      {mode === "timeline" ? (
        <div className="w-full flex items-center gap-3">
          <span className="text-xs text-greyscale-3 w-12 text-right">0.0s</span>
          <input
            className="w-full accent-primary"
            type="range"
            min={0}
            max={durationS}
            step={0.1}
            value={tSec}
            onChange={(e) => setTSec(Number(e.target.value || 0))}
          />
          <span className="text-xs text-greyscale-3 w-12">
            {durationS.toFixed(0)}s
          </span>
        </div>
      ) : (
        <>
          <div className="w-full flex items-center gap-3">
            <button
              type="button"
              className="text-sm underline disabled:opacity-40"
              onClick={() => setActiveIdxManual((i) => Math.max(0, i - 1))}
              disabled={activeIdxManual <= 0}
            >
              {t("results.flowLoops.prev", { defaultValue: "Prev" })}
            </button>

            <input
              className="w-full accent-primary"
              type="range"
              min={0}
              max={Math.max(0, n - 1)}
              step={1}
              value={activeIdxManual}
              onChange={(e) => setActiveIdxManual(Number(e.target.value || 0))}
            />

            <button
              type="button"
              className="text-sm underline disabled:opacity-40"
              onClick={() => setActiveIdxManual((i) => Math.min(n - 1, i + 1))}
              disabled={activeIdxManual >= n - 1}
            >
              {t("results.flowLoops.next", { defaultValue: "Next" })}
            </button>
          </div>

          {reveal === "progress" && (
            <div className="w-full flex items-center gap-3">
              <span className="text-xs text-greyscale-3 w-12 text-right">
                0%
              </span>
              <input
                className="w-full accent-primary"
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={loopProgress}
                onChange={(e) => setLoopProgress(Number(e.target.value))}
              />
              <span className="text-xs text-greyscale-3 w-12">
                {Math.round(loopProgress * 100)}%
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default memo(UnifiedFlowOverlay);
